// auth.js
import axios from 'axios';
import { RETRIEVE_TOKEN, DESTROY_TOKEN } from '../mutation-types';
import { initEcho, refreshEcho } from '../../plugins/pusher';
import router from '../../router';

const state = {
    token: localStorage.getItem('access_token') || null,
    loadingAuth: false,
    loadingApp: false
};

const getters = {
    getToken: state => state.token,
    isLoggedIn: state => !!state.token, // Check if the token exists
    loadingAuth: state => state.loadingAuth,
    loadingApp: state => state.loadingApp
};

const actions = {
    initializeAuth({ commit, dispatch }) {
        return new Promise(resolve => {
            commit('SET_LOADING_AUTH', true);
            const token = localStorage.getItem('access_token');
            if (token) {
                commit(RETRIEVE_TOKEN, token);
                axios.defaults.headers.common[
                    'Authorization'
                ] = `Bearer ${token}`;
                initEcho(token); // Initialize Echo with the token
                dispatch('postLoginTasks')
                    .then(() => {
                        commit('SET_LOADING_AUTH', false);
                        resolve(); // Resolve after successful setup
                    })
                    .catch(error => {
                        console.error('Error during postLoginTasks:', error);
                        commit('SET_LOADING_AUTH', false);
                        router.push({ name: 'Login' }); // Redirect if something goes wrong
                    });
            } else {
                // If there is no token, redirect to login page
                router.push({ name: 'Login' });
                commit('SET_LOADING_AUTH', false);
                resolve(); // Resolve regardless to avoid pending promises
            }
        });
    },

    login({ commit, dispatch }, data) {
        // Defines login type
        let url = '';
        switch (data.userType) {
            case 'internal':
                url = 'login';
                break;
            case 'customer':
                url = 'customers/login';
                break;
            default:
                url = 'login';
                break;
        }
        // console.log('url ', url);
        // console.log(data);

        return new Promise((resolve, reject) => {
            axios
                .post(url, data.credentials)
                .then(response => {
                    commit('SET_LOADING_APP', true);
                    // console.log('response auth', response.data);

                    // Set current user
                    const {
                        id,
                        name,
                        username,
                        email,
                        type,
                    } = response.data.user;
                    const currentUser = JSON.stringify({
                        id,
                        name,
                        username,
                        email,
                        type,
                    });
                    dispatch('setCurrentUser', currentUser, { root: true });
                    localStorage.setItem('current_user', currentUser);

                    const token = response.data.token;
                    const storage = data.credentials.remember
                        ? localStorage
                        : sessionStorage;

                    storage.setItem('access_token', token);
                    storage.setItem('roles', response.data.role);
                    storage.setItem(
                        'permissions',
                        JSON.stringify(response.data.permissions)
                    );

                    commit(RETRIEVE_TOKEN, token);
                    axios.defaults.headers.common[
                        'Authorization'
                    ] = `Bearer ${token}`;
                    refreshEcho(token); // Refresh Echo with the new token

                    // Inicializamos la autenticación y permisos antes de redirigir
                    dispatch('initializeAuth')
                        .then(() => {
                            // Esperar a que se completen las tareas post-login (cargar permisos, configurar Pusher)
                            return dispatch('postLoginTasks');
                        })
                        .then(() => {
                            // Resolver la promesa del login solo cuando todo haya terminado
                            commit('SET_LOADING_APP', false);
                            resolve(response);
                        })
                        .catch(error => {
                            console.error('Error during initializeAuth:', error);
                            commit('SET_LOADING_APP', false);
                            reject(error);
                        });
                })
                .catch(error => {
                    commit('SET_LOADING_APP', false);
                    reject(error);
                });
        });
    },
    logout({ commit, state, rootGetters }) {
        let loginRoute = '/login';
        const currentUser = rootGetters.getCurrentUser;
        if (currentUser && currentUser.type) {
            switch (currentUser.type) {
                case 'internal':
                    loginRoute = '/login';
                    break;
                case 'customer':
                    loginRoute = '/customer/login';
                    break;  
                default:
                    loginRoute = '/login';
                    break;
            }
        }
        console.log('Login route: ', loginRoute);
        return new Promise((resolve, reject) => {
            axios
                .post('/logout', '', {
                    headers: { Authorization: `Bearer ${state.token}` }
                })
                .then(response => {
                    localStorage.removeItem('access_token');
                    localStorage.removeItem('roles');
                    localStorage.removeItem('permissions');
                    localStorage.removeItem('current_user');
                    commit(DESTROY_TOKEN);
                    delete axios.defaults.headers.common['Authorization'];
                    refreshEcho(null); // Refresh Echo with no token
                    router.push(loginRoute);
                    resolve(response);
                })
                .catch(error => {
                    localStorage.removeItem('access_token');
                    commit(DESTROY_TOKEN);
                    delete axios.defaults.headers.common['Authorization'];
                    refreshEcho(null); // Refresh Echo with no token
                    router.push(loginRoute);
                    reject(error);
                });
        });
    },

    postLoginTasks({ dispatch }) {
        return dispatch('permissions/loadUserRolesAndPermissions', null, {
            root: true
        })
            .then(() => {
                dispatch('setupPusherEventListener', null, { root: true });
            })
            .catch(error => {
                console.error('Error in postLoginTasks:', error);
            });
    }
};

const mutations = {
    [RETRIEVE_TOKEN](state, token) {
        state.token = token;
    },
    [DESTROY_TOKEN](state) {
        state.token = null;
    },
    SET_LOADING_AUTH(state, value) {
        state.loadingAuth = value;
    },
    SET_LOADING_APP(state, value) {
        state.loadingApp = value; // Mutación para manejar el estado de carga global
    }
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};
